import { createSlice } from '@reduxjs/toolkit';
import {
  PENDING,
  RUNNING,
  DONE,
  UNKNOWN_LEVEL,
  SUCCESS_LEVEL,
  WARNING_LEVEL,
  ERROR_LEVEL,
  RETRY,
  YES,
  NO,
  SPEAKERS_CHECK,
} from 'UTILS/constants';
import { deviceListUpdate } from 'REDUCERS/appSlice';
import { getStoredDeviceId } from 'UTILS/callbacks';

export const initialState = {
  stage: PENDING,
  level: UNKNOWN_LEVEL,
  resultList: [],
  isPrompting: false,
  isPlaying: true,
  audioOutputList: [],
  selectedAudioOutputId: '',
};

const browserSlice = createSlice({
  name: SPEAKERS_CHECK,
  initialState,
  reducers: {
    start(state) {
      state.resultList = [];
      state.level = UNKNOWN_LEVEL;
      state.stage = RUNNING;
      state.isPlaying = true;
      state.isPrompting = true;
    },
    donePlaying(state) {
      state.isPlaying = false;
    },
    feedback(state, { payload: { answer } }) {
      switch (answer) {
        case RETRY:
          state.isPlaying = true;
          break;
        case YES:
          state.resultList.push({ level: SUCCESS_LEVEL, type: 'tone_heard' });
          break;
        case NO:
          state.resultList.push({ level: WARNING_LEVEL, type: 'tone_not_heard' });
          break;
      }
    },
    success(state, { payload }) {
      state.resultList.push({ level: SUCCESS_LEVEL, ...payload });
      state.level = Math.max(state.level, SUCCESS_LEVEL);
    },
    warning(state, { payload }) {
      state.resultList.push({ level: WARNING_LEVEL, ...payload });
      state.level = Math.max(state.level, WARNING_LEVEL);
    },
    error(state, { payload }) {
      state.resultList.push({ level: ERROR_LEVEL, ...payload });
      state.level = ERROR_LEVEL;
    },
    selectAudioOutput(state, { payload }) {
      state.selectedAudioOutputId = payload.deviceId;
    },
    end(state) {
      state.stage = DONE;
      state.isPrompting = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(deviceListUpdate, (state, { payload: { deviceList } }) => {
      const newAudioOutputList = deviceList.filter(({ kind }) => kind === 'audiooutput');
      if (newAudioOutputList.length !== state.audioOutputList.length ||
        state.audioOutputList.some(({ deviceId }) => !newAudioOutputList.find(({ deviceId: newDeviceId }) => deviceId === newDeviceId))) {
        // The list has changed
        if (!newAudioOutputList.some(({ deviceId }) => deviceId === state.selectedAudioOutputId)) {
          // The selected source was removed
          state.selectedAudioOutputId = '';
        }
        if (!state.selectedAudioOutputId) {
          const defaultOutputId = getStoredDeviceId('audioOutput');
          if (defaultOutputId && newAudioOutputList.some(({ deviceId }) => deviceId === defaultOutputId)) {
            // A default exists in local storage, we use it
            state.selectedAudioOutputId = defaultOutputId;
          } else if (newAudioOutputList.length > 0) {
            // We use the first source of the list
            state.selectedAudioOutputId = newAudioOutputList[0].deviceId;
          }
        }
        state.audioOutputList = newAudioOutputList;
      }
    });
    builder.addDefaultCase(() => {});
  },
});

export const { start, success, warning, error, donePlaying, feedback, selectAudioOutput, end } = browserSlice.actions;
export default browserSlice.reducer;
